import { chakra, Heading, Text, Box, Link, Container, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { GoogleReCaptchaProvider } from '../../../../modules/core/hooks/useReCaptcha';
import { ssrContext } from '../../../../modules/core/contexts/GlobalContext';
import { ContactForm } from '../../../../modules/contact/components/ContactForm';
import { breakpoint } from '../../../../modules/design/utils/breakpoint';

export const Contact = () => {
    const { t } = useTranslation();

    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={ssrContext.recaptchaKey}
            scriptProps={{ async: true }}
        >
            <Box bg="white" py={20}>
                <Container maxW="container.xl">
                    <FlexLayout>
                        <Box flex={1}>
                            <PageHeading as="h1">
                                {t('contact.main.title')}
                                <br />
                                <chakra.span fontWeight="bold">
                                    {t('contact.main.subtitle')}
                                </chakra.span>
                            </PageHeading>

                            <Card>
                                <Heading as="h2" size="md" color="general.dark" mb={6}>
                                    {t('contact.card.title')}
                                </Heading>
                                <Text fontSize="sm" color="general.subtext" mb={10}>
                                    {t('contact.card.subtext')}
                                </Text>
                                <CardLink href={t('common.help_link')}>
                                    {t('contact.card.button')}
                                </CardLink>
                            </Card>
                        </Box>

                        <Box id="contact-form" flex={1}>
                            <FormContainer>
                                <ContactForm />
                            </FormContainer>

                            <Text
                                fontSize="xs"
                                color="general.subtext"
                                textAlign="center"
                                lineHeight="2rem"
                            >
                                {t('contact.form.info')}
                                <br />
                                {t('contact.form.info-reply')}
                            </Text>
                        </Box>
                    </FlexLayout>
                </Container>

                <chakra.hr color="general.lightGrayAlt" />

                <Container pt={10} maxW="container.xl" textAlign="center">
                    <Heading as="h2" size="md" mb={6} color="general.dark">
                        {t('contact.call.title')}
                    </Heading>
                    <Text color="general.subtext" mb={4}>
                        {t('contact.call.subtext')}
                    </Text>
                    <PhoneLink href="tel:+48717232234" target="_blank">
                        +48 717 232 234
                    </PhoneLink>
                </Container>
            </Box>
        </GoogleReCaptchaProvider>
    );
};

const FlexLayout = styled(Flex)`
    flex-direction: column;
    gap: var(--ck-space-10);
    margin-bottom: var(--ck-space-10);

    ${breakpoint('lg')} {
        flex-direction: row;
        gap: 11.2rem;
        margin-bottom: var(--ck-space-10);
    }
`;

const PageHeading = styled(Heading)`
    font-size: var(--ck-fontSizes-5xl);
    font-weight: normal;
    line-height: 5.2rem;
    margin-bottom: var(--ck-space-16);
    color: var(--ck-colors-general-dark);
`;

const Card = styled(Box)`
    padding: var(--ck-space-10);
    align-self: stretch;
    border-radius: var(--ck-space-2);
    border: 1px solid var(--ck-colors-general-lightGrayAlt);
`;

const CardLink = styled(Link)`
    color: var(--ck-colors-general-dark) !important;
    text-decoration: underline;
    font-size: 1.8rem;
    font-style: normal;
    font-weight: 600;
    line-height: 2.8rem;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: auto;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;

    &:hover,
    &:focus {
        text-decoration: none;
    }
`;

const FormContainer = styled(Box)`
    border-radius: 0.8rem;
    background: var(--ck-colors-general-contractorBg);
    margin-bottom: var(--ck-space-4);
    padding: var(--ck-space-10);
    min-height: 49.8rem;

    ${breakpoint('lg')} {
        padding: var(--ck-space-16);
    }
`;

const PhoneLink = styled(Link)`
    color: var(--ck-colors-general-dark) !important;
    font-size: 2rem;
    font-style: normal;
    font-weight: 700;
    line-height: 2.8rem;

    &:hover,
    &:focus {
        text-decoration: underline;
    }
`;
